import React, { useEffect, useRef, memo, useState } from 'react';
import useCanvasDrawing from '../hooks/useCanvasDrawing';
import SelectedOptions from './SelectedOptions';
import { RENDER_ORDER } from '../config/stepsConfig';
import { Download, CloudLightning, Mail, Trash2 } from 'lucide-react';
import { Tooltip } from 'react-tooltip';

function Canvas({ totalSteps, currentStep, theSize, setSelectedOptions, selectedOptions, stepsData, collectionId, onRandomize, initialSelectedOptions }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  
  // Calculate responsive canvas size
  const [canvasSize, setCanvasSize] = useState(theSize);
  const [isMobile, setIsMobile] = useState(false);
  
  const { drawToCanvas } = useCanvasDrawing(canvasSize);
  const [renderOrder, setRenderOrder] = useState(RENDER_ORDER);
  const [orderedSteps, setOrderedSteps] = useState(stepsData);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 830);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Update canvas size based on container dimensions
  useEffect(() => {
    const updateCanvasSize = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const containerHeight = containerRef.current.offsetHeight;
        
        // Use the smaller dimension to ensure the square fits in the container
        const availableSize = Math.min(containerWidth, containerHeight);
        
        // Calculate size based on available space and device type
        let newSize;
        if (isMobile) {
          // On mobile, use most of the available space with some padding
          newSize = Math.min(availableSize * 1, 500); // Max 500px on mobile
        } else {
          // On desktop, use most of the available space with some padding
          newSize = Math.min(availableSize * 1, 750); // Max 750px on desktop
        }
        
        // Ensure minimum size
        newSize = Math.max(newSize, 250);
        
        setCanvasSize(newSize);
      }
    };

    updateCanvasSize();
    
    // Use ResizeObserver for better container size tracking
    let resizeObserver;
    if (containerRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(updateCanvasSize);
      resizeObserver.observe(containerRef.current);
    }
    
    // Fallback to window resize listener
    window.addEventListener('resize', updateCanvasSize);
    
    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', updateCanvasSize);
    };
  }, [isMobile]);

  useEffect(() => {
    setOrderedSteps(stepsData);
  }, [stepsData, collectionId]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    drawToCanvas(ctx, selectedOptions, renderOrder, collectionId);
  }, [selectedOptions, canvasSize, drawToCanvas, renderOrder, collectionId]);

  const downloadAsset = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const dataURL = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = dataURL;
    a.download = 'download.png';
    a.click();
  };

  const requestAsset = () => {
    alert('This Will Be The Popup');
  }

  const resetAsset = () => {
    setSelectedOptions(initialSelectedOptions);
  }

  const isAvatarComplete = () => {
    if (!selectedOptions) return false;
    if (!selectedOptions.background) return false;
    if (!selectedOptions.body) return false;
    if (!selectedOptions.eyes) return false;
    if (!selectedOptions.mouth) return false;
    return true;
  };

  const handleReorder = (newOrder) => {
    setRenderOrder(newOrder);
    
    const newOrderedSteps = newOrder.map(stepName => 
      stepsData.find(step => step.stepName === stepName)
    );
    setOrderedSteps(newOrderedSteps);
  };

  return (
    <div ref={containerRef} className="w-full flex flex-col items-center justify-center pb-5">
      <div 
        id="canvas-wrap" 
        className="border-4 border-purple overflow-hidden flex relative"
        style={{ 
          width: canvasSize,
          height: canvasSize,
          aspectRatio: '1 / 1',
          flexShrink: 0
        }}
      >
        <button
          onClick={onRandomize}
          className="absolute top-2 left-2 bg-black hover:bg-blue text-white px-4 py-2 font-bold transition-colors duration-200"
          data-tooltip-id="randomize-tooltip"
          data-tooltip-content="Random Cloudy"
        >
          <CloudLightning className="w-6 h-6" />
        </button>

        <canvas 
          ref={canvasRef} 
          width={canvasSize} 
          height={canvasSize}
          className="w-full h-full block"
          style={{
            aspectRatio: '1 / 1',
            objectFit: 'contain'
          }}
        />
        {isAvatarComplete() && (
          <div className="absolute flex gap-2 bottom-2 right-2">
            <button 
              className="bg-black hover:bg-blue text-white px-4 py-3 font-bold cursor-pointer transition-colors duration-300"
              onClick={resetAsset}
              data-tooltip-id="reset-tooltip"
              data-tooltip-content="Reset to default"
            >
              <Trash2 className="w-6 h-6" />
            </button>

            <button 
              className="bg-black hover:bg-blue text-white px-4 py-3 font-bold cursor-pointer transition-colors duration-300"
              onClick={downloadAsset}
              data-tooltip-id="download-tooltip"
              data-tooltip-content="Download image"
            >
              <Download className="w-6 h-6" />
            </button>

            <button
              className="flex items-center gap-2 bg-black hover:bg-blue text-white px-4 py-3 font-bold cursor-pointer transition-colors duration-300"
              onClick={requestAsset}
              data-tooltip-id="request-tooltip"
              data-tooltip-content="Request this toon"
            >
              <Mail className="w-6 h-6" />
            </button>
          </div>
        )}
      </div>

      <SelectedOptions
        selectedOptions={selectedOptions}
        stepsData={orderedSteps}
        onReorder={handleReorder}
        collectionId={collectionId}
      />

      {/* React Tooltips */}
      <Tooltip id="randomize-tooltip" place="right" />
      <Tooltip id="reset-tooltip" place="top" />
      <Tooltip id="download-tooltip" place="top" />
      <Tooltip id="request-tooltip" place="top" />
    </div>
  );
}

export default memo(Canvas);
